"use client"

import type React from "react"

import OrgLayout from "@/layouts/OrgLayout"
import { useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { Plus, Trash2, Edit, Mail, X, Search } from "lucide-react"
//import {  mockFacilities } from "@/lib/mock-org-data"
import type { Email, Facility } from "@/types"
import { Checkbox } from "@/components/ui/checkbox"

interface EmailsProps {
	activeView: string
	emails: Email[]
	facilities: Facility[]
}

function Emails({ emails: initialEmails, facilities }: EmailsProps) {
	const [emails, setEmails] = useState<Email[]>(initialEmails)
	const [searchQuery, setSearchQuery] = useState("")
	const [dialogOpen, setDialogOpen] = useState(false)
	const [editing, setEditing] = useState<Email | null>(null)
	const [selectedFacilities, setSelectedFacilities] = useState<number[]>([])
	const [facilitySearch, setFacilitySearch] = useState("")
	const [isGroup, setIsGroup] = useState(false)

	const activeView = window.location.pathname.split("/").pop() || "direct-emails"
	console.log("facilies", facilities)
	console.log("emails", emails)
	const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		const formData = new FormData(e.currentTarget)
		const address = formData.get("email") as string
		const groupName = formData.get("group_name") as string

		if (editing) {
			setEmails(
				emails.map((em) =>
					em.id === editing.id
						? {
							...em,
							address,
							is_group: isGroup,
							group_name: isGroup ? groupName : undefined,
							facility_ids: selectedFacilities,
							updated_at: new Date().toISOString(),
						} as Email
						: em,
				),
			)
		} else {
			const newEmail: Partial<Email> = {
				address,
				is_group: isGroup,
				group_name: isGroup ? groupName : undefined,
				facility_ids: selectedFacilities,
				created_at: new Date().toISOString(),
				updated_at: new Date().toISOString(),
			}
			console.log(newEmail)
			//setEmails([...emails, newEmail])
		}
		setDialogOpen(false)
		setEditing(null)
		setSelectedFacilities([])
		setIsGroup(false)
	}

	const addFacility = (facilityId: number) => {
		if (!selectedFacilities.includes(facilityId)) {
			setSelectedFacilities([...selectedFacilities, facilityId])
		}
		setFacilitySearch("")
	}

	const removeFacility = (facilityId: number) => {
		setSelectedFacilities(selectedFacilities?.filter((id) => id !== facilityId))
	}

	//const filteredFacilities = mockFacilities.filter(
	//  (f) => f.name?.toLowerCase().includes(facilitySearch?.toLowerCase()) && !selectedFacilities.includes(f.id),
	//)
	const filteredFacilities = facilities?.filter(
		(f) => f.name?.toLowerCase().includes(facilitySearch?.toLowerCase()) && !selectedFacilities.includes(f.id),
	)

	const getFilteredEmails = () => {
		let filtered = emails
		console.log("activeView", activeView)

		if (activeView === "direct-emails") {
			filtered = emails.filter((email) => !email.is_group)
			return filtered

		} else if (activeView === "email-groups") {
			filtered = emails.filter((email) => email.is_group)
		}
		// "all-emails" shows everything

		return filtered.filter((email) => {
			return (
				email.address?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
				(email.group_name && email.group_name?.toLowerCase().includes(searchQuery?.toLowerCase()))
			)
		})
	}

	const filteredEmails = getFilteredEmails()
	console.log("filteredEmails", filteredEmails)

	const getViewInfo = () => {
		switch (activeView) {
			case "direct-emails":
				return {
					title: "Direct Emails",
					description: "Dedicated email addresses for facilities",
				}
			case "email-groups":
				return {
					title: "Email Groups",
					description: "Email groups shared across multiple facilities",
				}
			case "all-emails":
				return {
					title: "All Emails",
					description: "All email addresses and groups",
				}
			default:
				return {
					title: "Email Addresses",
					description: "Manage email addresses and their facility assignments",
				}
		}
	}

	const viewInfo = getViewInfo()

	return (
		<div className="space-y-6 w-full">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-2xl font-semibold">{viewInfo.title}</h2>
					<p className="text-sm text-muted-foreground">{viewInfo.description}</p>
				</div>
				<Dialog
					open={dialogOpen}
					onOpenChange={(open) => {
						setDialogOpen(open)
						if (!open) {
							setEditing(null)
							setSelectedFacilities([])
							setIsGroup(false)
						}
					}}
				>
					<DialogTrigger asChild>
						<Button
							onClick={() => {
								setEditing(null)
								setSelectedFacilities([])
								setIsGroup(false)
							}}
						>
							<Plus className="h-4 w-4 mr-2" />
							Add Email
						</Button>
					</DialogTrigger>
					<DialogContent className="max-w-2xl">
						<form onSubmit={handleSave}>
							<DialogHeader>
								<DialogTitle>{editing ? "Edit Email" : "Add Email"}</DialogTitle>
								<DialogDescription>
									{editing ? "Update email information" : "Add a new email address or group"}
								</DialogDescription>
							</DialogHeader>
							<div className="space-y-4 py-4">
								<div className="flex items-center space-x-2">
									<Checkbox
										id="is_group"
										checked={isGroup}
										onCheckedChange={(checked) => setIsGroup(checked as boolean)}
									/>
									<Label htmlFor="is_group" className="text-sm font-normal cursor-pointer">
										This is an email group (shared across facilities)
									</Label>
								</div>

								{isGroup && (
									<div className="space-y-2">
										<Label htmlFor="group_name">Group Name</Label>
										<Input
											id="group_name"
											name="group_name"
											defaultValue={editing?.group_name}
											placeholder="e.g., Referrals Team"
											required={isGroup}
										/>
									</div>
								)}

								<div className="space-y-2">
									<Label htmlFor="email">Email Address</Label>
									<Input
										id="email"
										name="email"
										type="email"
										defaultValue={editing?.address}
										placeholder="fax@clinic.com"
										required
									/>
								</div>
								<div className="space-y-2">
									<Label>Assigned Facilities</Label>
									<div className="space-y-2">
										{selectedFacilities.length > 0 && (
											<div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/30">
												{selectedFacilities.map((facilityId) => {
													const facility = facilities.find((f) => f.id === facilityId)
													return facility ? (
														<Badge key={facilityId} variant="secondary" className="gap-1">
															{facility.name}
															<button
																type="button"
																onClick={() => removeFacility(facilityId)}
																className="ml-1 hover:bg-destructive/20 rounded-full"
															>
																<X className="h-3 w-3" />
															</button>
														</Badge>
													) : null
												})}
											</div>
										)}
										<div className="relative">
											<Input
												placeholder="Search facilities to add..."
												value={facilitySearch}
												onChange={(e) => setFacilitySearch(e.target.value)}
											/>
											{facilitySearch && filteredFacilities.length > 0 && (
												<div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
													{filteredFacilities.map((facility) => (
														<button
															key={facility.id}
															type="button"
															onClick={() => addFacility(facility.id)}
															className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
														>
															{facility.name}
														</button>
													))}
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
							<DialogFooter>
								<Button type="submit">{editing ? "Update" : "Create"}</Button>
							</DialogFooter>
						</form>
					</DialogContent>
				</Dialog>
			</div>

			<div className="flex items-center gap-2">
				<Search className=" h-4 w-4 text-muted-foreground" />
				<Input
					placeholder="Search email addresses..."
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					className="pl-9"
				/>
			</div>

			<div className="space-y-2">
				{filteredEmails.map((email) => {
					const filtered_facilities = facilities.filter((f) => email.facility_ids?.includes(f.id))
					return (
						<Card key={email.id} className="p-4">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-4 flex-1">
									<Mail className="h-5 w-5 text-muted-foreground" />
									<div className="flex-1">
										<div className="flex items-center gap-2">
											<p className="font-medium">{email.address}</p>
											{email.is_group && (
												<Badge variant="secondary" className="text-xs">
													{email.group_name || "Group"}
												</Badge>
											)}
										</div>
										{facilities.length > 0 && (
											<div className="flex flex-wrap gap-1 mt-1">
												{filtered_facilities.map((f) => (
													<Badge key={f.id} variant="outline" className="text-xs">
														{f.name}
													</Badge>
												))}
											</div>
										)}
									</div>
								</div>
								<div className="flex gap-2">
									<Button
										variant="ghost"
										size="icon"
										onClick={() => {
											setEditing(email)
											setSelectedFacilities(email.facility_ids || [])
											setIsGroup(email.is_group || false)
											setDialogOpen(true)
										}}
									>
										<Edit className="h-4 w-4" />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										onClick={() => setEmails(emails.filter((e) => e.id !== email.id))}
									>
										<Trash2 className="h-4 w-4 text-destructive" />
									</Button>
								</div>
							</div>
						</Card>
					)
				})}
			</div>
		</div>
	)
}
Emails.layout = (page: React.ReactNode) => <OrgLayout activeSection="emails">{page}</OrgLayout>
export default Emails
